// Capability mapping for workspace roles
// Focuses on navigation & page section gating

export type Capability =
  | 'users.view.global'
  | 'users.view.collaborators'
  | 'users.manage'
  | 'users.invite'
  | 'users.roles.change'
  | 'analytics.view.business'
  | 'analytics.view.projects'
  | 'analytics.view.users'
  | 'analytics.view.system'
  // AX1 Control Plane. These capabilities expose operational metadata only;
  // they never imply access to customer evidence, agreements or private notes.
  | 'platform.control.view'
  | 'platform.customers.view'
  | 'platform.customers.manage'
  | 'platform.customers.register'
  | 'platform.provisioning.manage'
  | 'platform.commercial.view'
  | 'platform.commercial.manage'
  | 'platform.commercial.approve'
  | 'platform.operations.view'
  | 'platform.operations.manage'
  | 'platform.access.view'
  | 'platform.access.request'
  | 'platform.access.approve'
  | 'platform.releases.view'
  | 'platform.releases.manage'
  | 'platform.roles.assign'
  | 'platform.roles.activate'
  | 'platform.roles.activate.approve'
  // Customer commercial account. Financial and contract visibility is kept
  // separate from operational package/usage visibility.
  | 'commercial.summary.view'
  | 'commercial.entitlements.view'
  | 'commercial.usage.view'
  | 'commercial.contract.view'
  | 'commercial.pricing.view'
  | 'commercial.invoices.view'
  | 'commercial.change_request.create'
  | 'commercial.billing_profile.manage'
  | 'commercial.renewal.manage'
  // File capabilities
  | 'files.view'
  | 'files.create'
  | 'files.update'
  | 'files.delete'
  | 'documents.restore'
  | 'documents.version.restore'
  | 'documents.sign'
  // Project capabilities
  | 'projects.view'
  | 'projects.create'
  | 'projects.update'
  | 'projects.delete'
  // Canonical Investment Rights Registry. Read, authoring, and legal
  // activation are intentionally separate authorities.
  | 'investments.rights.view'
  | 'investments.rights.manage'
  | 'investments.rights.activate'
  // Financial execution authorities. These are deliberately separated from
  // ordinary organization administration and user management.
  | 'capital.beneficiaries.manage'
  | 'capital.disbursements.manage'
  | 'capital.settlements.manage'
  | 'capital.vaults.manage'
  // Project member management capabilities
  | 'projects.members.role.change'
  | 'projects.members.remove'
  // Task capabilities (use 'update' for consistency with projects)
  | 'tasks.view'
  | 'tasks.create'
  | 'tasks.update'
  | 'tasks.delete'
  | 'tasks.assign'
  // Company capabilities (CRM)
  | 'companies.view'
  | 'companies.manage'
  // Report capabilities - canonical reporting access with deterministic company/project/investment scope
  | 'reports.view'
  // Calendar capabilities
  | 'calendar.view'
  | 'calendar.manage'
  // Security Center capabilities. These are deliberately granular because
  // viewing audit activity must not imply the ability to change identity,
  // privacy, incident, integration, or assurance controls.
  | 'security.overview.view'
  | 'security.identity.view'
  | 'security.identity.manage'
  | 'security.audit.view'
  | 'security.audit.export'
  | 'security.privacy.view'
  | 'security.privacy.manage'
  | 'security.integrations.view'
  | 'security.integrations.manage'
  | 'security.api_keys.view'
  | 'security.api_keys.manage'
  | 'security.encryption.view'
  | 'security.encryption.manage'
  | 'security.encryption.rotate'
  | 'security.incidents.view'
  | 'security.incidents.manage'
  | 'security.compliance.view'
  | 'security.compliance.manage'
  | 'security.assurance.view'
  | 'security.assurance.generate'
  | 'security.assurance.download'
  | 'security.privileged_access.approve'
  // Compatibility aliases for older modules. New route and section guards
  // must use the granular capabilities above.
  | 'security.view'
  | 'security.manage'
  | 'debug.view'
  | 'debug.manage'
  // Evidence pack sharing capabilities
  | 'evidence.share.create'
  | 'evidence.share.revoke'
  | 'evidence.share.view'
  | 'evidence.share.export'
  // Data room access grants capabilities
  | 'dataroom.access.grant'
  | 'dataroom.access.revoke'
  | 'dataroom.access.view'
  | 'dataroom.access.export'
  // Escalation workflow capabilities
  | 'escalations.view'
  | 'escalations.create'
  | 'escalations.acknowledge'
  | 'escalations.resolve'
  | 'escalations.reassign'
  | 'escalations.cancel'
  | 'escalations.admin'
  // System settings & role management
  | 'system.settings.edit'
  | 'system.roles.manage'
  // Investor dashboard
  | 'investor.dashboard.view'
  // Portal-scoped capital participant capabilities
  | 'investor.investments.view'
  | 'investor.milestones.view'
  | 'investor.distributions.view'
  | 'investor.documents.view'
  | 'investor.calendar.view'
  | 'investor.chat.access'
  | 'investor.security.view'
  | 'investor.settings.manage'
  | 'investor.reports.view'
  | 'investor.notifications.view'
  // Governance gate capabilities
  | 'gates.view'
  | 'gates.create'
  | 'gates.configure'
  | 'gates.lock'
  | 'gates.amend'
  | 'gates.review';

import type {
  MvpAccountRole,
  PlatformAccountRole,
  PlatformCapabilityAssignment,
  PlatformRole,
  UserRole,
  WorkspaceRole,
} from '../../types';
import { MVP_PLATFORM_ACCOUNT_ROLES } from '../../types';
import { normalizeAccountRole, resolveMvpAccountRole } from './accountRoles';
export type { WorkspaceRole } from '../../types';

const PLATFORM_CAPABILITIES: Record<PlatformRole, Capability[]> = {
  // Control-plane access never implies customer workspace or Capital Gate access.
  platform_owner: [
    'users.view.global', 'users.manage', 'users.invite', 'users.roles.change',
    'analytics.view.system', 'analytics.view.users',
    'platform.control.view', 'platform.customers.view', 'platform.commercial.view',
    'platform.commercial.approve', 'platform.operations.view',
    'platform.access.view',
    'platform.releases.view', 'platform.roles.assign',
    'platform.roles.activate', 'platform.roles.activate.approve',
    'security.overview.view', 'security.identity.view', 'security.audit.view',
    'security.audit.export', 'security.privacy.view', 'security.integrations.view',
    'security.api_keys.view', 'security.encryption.view',
    'security.incidents.view', 'security.compliance.view',
    'security.assurance.view', 'security.assurance.generate', 'security.assurance.download',
    'security.view', 'debug.view', 'debug.manage',
    'system.settings.edit', 'system.roles.manage',
  ],
  platform_admin: [
    'users.view.global', 'users.manage', 'users.invite', 'users.roles.change',
    'analytics.view.system', 'analytics.view.users',
    'platform.control.view', 'platform.customers.view', 'platform.customers.manage',
    'platform.customers.register', 'platform.provisioning.manage',
    'platform.commercial.view', 'platform.commercial.manage',
    'platform.operations.view', 'platform.operations.manage',
    'platform.access.view', 'platform.access.request', 'platform.access.approve',
    'platform.releases.view', 'platform.releases.manage', 'platform.roles.assign',
    'platform.roles.activate', 'platform.roles.activate.approve',
    'security.overview.view', 'security.identity.view', 'security.identity.manage',
    'security.audit.view', 'security.audit.export', 'security.privacy.view',
    'security.privacy.manage', 'security.integrations.view', 'security.integrations.manage',
    'security.api_keys.view', 'security.api_keys.manage',
    'security.encryption.view', 'security.encryption.manage',
    'security.incidents.view', 'security.incidents.manage', 'security.compliance.view',
    'security.compliance.manage', 'security.assurance.view', 'security.assurance.generate',
    'security.assurance.download',
    'security.view', 'security.manage', 'debug.view', 'debug.manage',
    'system.settings.edit', 'system.roles.manage',
  ],
  system_admin: [
    'users.view.global',
    'analytics.view.system', 'analytics.view.users',
    'platform.control.view', 'platform.customers.view',
    'platform.operations.view', 'platform.operations.manage',
    'platform.provisioning.manage',
    'platform.access.view', 'platform.access.request',
    'platform.roles.activate',
    'platform.releases.view', 'platform.releases.manage',
    'security.overview.view', 'security.identity.view', 'security.audit.view',
    'security.audit.export', 'security.integrations.view', 'security.integrations.manage',
    'security.api_keys.view', 'security.api_keys.manage',
    'security.encryption.view', 'security.encryption.rotate',
    'security.incidents.view', 'security.incidents.manage', 'security.compliance.view',
    'security.assurance.view', 'security.assurance.download',
    'security.view', 'security.manage', 'debug.view', 'debug.manage',
    'system.settings.edit',
  ],
  security_admin: [
    'users.view.global', 'analytics.view.system',
    'platform.control.view', 'platform.customers.view',
    'platform.operations.view', 'platform.access.view', 'platform.access.approve',
    'platform.roles.activate', 'platform.roles.activate.approve',
    'platform.releases.view',
    'security.overview.view', 'security.identity.view', 'security.identity.manage',
    'security.audit.view', 'security.audit.export',
    'security.privacy.view', 'security.privacy.manage',
    'security.integrations.view', 'security.api_keys.view',
    'security.api_keys.manage',
    'security.encryption.view', 'security.encryption.manage', 'security.encryption.rotate',
    'security.incidents.view', 'security.incidents.manage',
    'security.compliance.view', 'security.compliance.manage',
    'security.assurance.view', 'security.assurance.generate', 'security.assurance.download',
    'security.view', 'security.manage',
  ],
  support_admin: [
    'users.view.global', 'users.invite',
    'platform.control.view', 'platform.customers.view',
    'platform.operations.view', 'platform.access.view', 'platform.access.request',
    'platform.roles.activate',
    'security.overview.view', 'security.identity.view',
    'security.audit.view', 'security.integrations.view', 'security.incidents.view',
    'security.view',
  ],
  commercial_admin: [
    'users.view.global', 'analytics.view.users',
    'platform.control.view', 'platform.customers.view', 'platform.customers.manage',
    'platform.customers.register',
    'platform.commercial.view', 'platform.commercial.manage',
    'platform.roles.activate',
    'platform.operations.view', 'platform.releases.view',
    'security.overview.view', 'security.audit.view', 'security.view',
  ],
  implementation_admin: [
    'users.view.global', 'users.invite',
    'platform.control.view', 'platform.customers.view', 'platform.customers.manage',
    'platform.provisioning.manage',
    'platform.commercial.view', 'platform.operations.view',
    'platform.access.view', 'platform.access.request',
    'platform.roles.activate',
    'platform.releases.view',
    'security.overview.view', 'security.identity.view', 'security.audit.view',
    'security.integrations.view', 'security.integrations.manage',
    'security.api_keys.view', 'security.view',
  ],
};

// ─────────── Canonical platform capability policy (P2/P3/P4 cutover) ──────────
/**
 * Platform-control capabilities: the subset of Capability that governs the AX1
 * Control Plane. Customer-resource capabilities (projects./files./tasks./
 * investments./calendar./companies./gates./documents./investor./escalations./
 * dataroom./evidence.) are intentionally EXCLUDED and can never be assigned or
 * resolved as a platform capability.
 */
export type PlatformCapability = Exclude<
  Extract<
    Capability,
    | `platform.${string}`
    | `security.${string}`
    | `system.${string}`
    | `users.${string}`
    | `analytics.${string}`
    | `debug.${string}`
  >,
  'analytics.view.projects' | 'analytics.view.business' | 'users.view.collaborators'
>;

/**
 * Reviewed BASE capability policy per CANONICAL platform account role (3 only).
 * Differentiated / sensitive powers are NOT here — they arrive via explicit
 * PlatformCapabilityAssignment records. `platform_functions` never contribute.
 */
export const PLATFORM_ROLE_CAPABILITIES: Record<PlatformAccountRole, readonly PlatformCapability[]> = {
  // Broad Control Plane administration. Still NO implicit customer-data access.
  platform_owner: [
    'users.view.global', 'users.manage', 'users.invite', 'users.roles.change',
    'analytics.view.system', 'analytics.view.users',
    'platform.control.view', 'platform.customers.view', 'platform.commercial.view',
    'platform.commercial.approve', 'platform.operations.view',
    'platform.access.view',
    'platform.releases.view', 'platform.roles.assign',
    'platform.roles.activate', 'platform.roles.activate.approve',
    'security.overview.view', 'security.identity.view', 'security.audit.view',
    'security.audit.export', 'security.privacy.view', 'security.integrations.view',
    'security.api_keys.view', 'security.encryption.view',
    'security.incidents.view', 'security.compliance.view',
    'security.assurance.view', 'security.assurance.generate', 'security.assurance.download',
    'security.view', 'debug.view', 'debug.manage',
    'system.settings.edit', 'system.roles.manage',
  ],
  // Reviewed operational base. Platform Admins operate customer commercial
  // accounts, but owner-only approval, role activation and security powers stay
  // outside this bundle. Sensitive `*.view` capabilities expose METADATA only.
  platform_admin: [
    'users.view.global',
    'analytics.view.system', 'analytics.view.users',
    'platform.control.view', 'platform.customers.view',
    'platform.commercial.view', 'platform.commercial.manage',
    'platform.operations.view',
    'platform.access.view', 'platform.access.request', 'platform.releases.view',
    'security.overview.view', 'security.identity.view', 'security.audit.view',
    'security.privacy.view', 'security.integrations.view', 'security.api_keys.view',
    'security.encryption.view', 'security.incidents.view', 'security.compliance.view',
    'security.assurance.view', 'security.view',
  ],
  // Narrower than platform_admin. Customer access only via exact JIT.
  support_operator: [
    'platform.control.view', 'platform.customers.view',
    'platform.access.view', 'platform.access.request',
    'platform.roles.activate',
    'security.overview.view',
  ],
};

/**
 * Runtime allowlist of platform-control capabilities, derived from the platform
 * capability catalog (LEGACY table = every platform capability that exists).
 * Used to reject unknown / customer-resource capability strings on assignments.
 */
const PLATFORM_CAPABILITY_SET: ReadonlySet<string> = new Set<string>(
  Object.values(PLATFORM_CAPABILITIES).flat(),
);

export function isPlatformCapability(cap: string | null | undefined): cap is PlatformCapability {
  return Boolean(cap && PLATFORM_CAPABILITY_SET.has(cap));
}

/** Sensitive platform capabilities that require AAL2 (step-up) to be effective. */
export const PLATFORM_AAL2_CAPABILITIES: ReadonlySet<string> = new Set<PlatformCapability>([
  'security.encryption.rotate',
  'platform.access.approve',
  'platform.commercial.approve',
]);

/** Final commercial authority is not delegable to routine Platform Admins. */
export const PLATFORM_OWNER_ONLY_CAPABILITIES: ReadonlySet<string> = new Set<PlatformCapability>([
  'platform.commercial.approve',
]);

export interface PlatformCapabilityResolveContext {
  now?: Date;
  aal?: 'aal1' | 'aal2' | 'aal3';
}

/**
 * Canonical platform capability resolution:
 *   base(canonical role) + active explicit assignments − expired/suspended/revoked.
 *
 * `platform_functions` are NEVER consulted. Assignments that are non-active,
 * unknown, customer-resource, not-yet-effective, expired, or AAL-insufficient
 * (for sensitive capabilities) are dropped (fail closed).
 */
export function resolvePlatformCapabilities(
  role: PlatformAccountRole | null | undefined,
  assignments: readonly PlatformCapabilityAssignment[] | null | undefined,
  ctx: PlatformCapabilityResolveContext = {},
): Set<PlatformCapability> {
  const out = new Set<PlatformCapability>();
  if (role && role in PLATFORM_ROLE_CAPABILITIES) {
    for (const cap of PLATFORM_ROLE_CAPABILITIES[role]) out.add(cap);
  }
  const now = ctx.now ?? new Date();
  const aalOk = ctx.aal === 'aal2' || ctx.aal === 'aal3';
  for (const assignment of assignments ?? []) {
    if (assignment.status !== 'active') continue;
    if (!isPlatformCapability(assignment.capability)) continue; // unknown / customer cap
    if (PLATFORM_OWNER_ONLY_CAPABILITIES.has(assignment.capability) && role !== 'platform_owner') continue;
    if (assignment.effectiveFrom && new Date(assignment.effectiveFrom).getTime() > now.getTime()) continue;
    if (assignment.effectiveUntil && new Date(assignment.effectiveUntil).getTime() <= now.getTime()) continue;
    if (PLATFORM_AAL2_CAPABILITIES.has(assignment.capability) && !aalOk) continue; // step-up required
    out.add(assignment.capability);
  }
  return out;
}

// Workspace Admin coordinates customer operations. Governed project and capital
// actions remain assignment-based; this base role never receives validation,
// signing, voting, investment-right activation, or capital-release powers.
const ORGANIZATION_ADMIN_CAPABILITIES: readonly Capability[] = [
  'users.view.global', 'users.manage', 'users.invite', 'users.roles.change',
  'analytics.view.business', 'analytics.view.projects', 'analytics.view.users',
  'reports.view',
  'projects.view', 'projects.create', 'companies.view', 'companies.manage',
  'files.view',
  'investments.rights.view',
  'tasks.view', 'tasks.create', 'tasks.update', 'tasks.delete', 'tasks.assign',
  'calendar.view', 'calendar.manage',
  'security.overview.view', 'security.identity.view', 'security.audit.view',
  'security.privacy.view', 'security.integrations.view', 'security.integrations.manage',
  'security.incidents.view', 'security.compliance.view', 'security.assurance.view',
  'security.privileged_access.approve', 'security.view', 'system.settings.edit',
  'commercial.summary.view', 'commercial.entitlements.view', 'commercial.usage.view',
  'commercial.change_request.create',
  'escalations.view', 'escalations.create', 'escalations.acknowledge',
  'escalations.resolve', 'escalations.reassign', 'escalations.cancel',
];

// Organisation Owner is the commercial account owner, not a super-admin.
// Aggregate oversight and Workspace Admin appointment are standing rights;
// project records and governed actions require their own scoped assignments.
const ORGANIZATION_OWNER_CAPABILITIES: readonly Capability[] = [
  'users.view.global', 'users.manage', 'users.invite', 'users.roles.change',
  'analytics.view.business', 'analytics.view.users',
  'security.overview.view', 'security.identity.view', 'security.audit.view',
  'security.privacy.view', 'security.view',
  'commercial.summary.view', 'commercial.entitlements.view', 'commercial.usage.view',
  'commercial.contract.view', 'commercial.pricing.view', 'commercial.invoices.view',
  'commercial.change_request.create', 'commercial.billing_profile.manage',
  'commercial.renewal.manage',
  'system.settings.edit',
];

const MVP_ACCOUNT_CAPABILITIES: Readonly<Record<MvpAccountRole, readonly Capability[]>> = {
  organization_owner: [...ORGANIZATION_OWNER_CAPABILITIES],
  organization_admin: [...ORGANIZATION_ADMIN_CAPABILITIES],
  // Every workspace participant can open Calendar + ChatHub. Resource-heavy
  // surfaces (projects/tasks/assets) still require scoped relationships.
  organization_member: ['calendar.view'],
  platform_owner: PLATFORM_CAPABILITIES.platform_owner,
  platform_admin: PLATFORM_CAPABILITIES.platform_admin,
  support_operator: [
    'platform.control.view', 'platform.access.view', 'platform.access.request',
    'platform.roles.activate',
  ],
};

const WORKSPACE_CAPABILITIES: Record<WorkspaceRole, Capability[]> = {
  organization_admin: [...ORGANIZATION_ADMIN_CAPABILITIES],
  customer_security_admin: [
    'users.view.global', 'analytics.view.users',
    'security.overview.view', 'security.identity.view', 'security.identity.manage',
    'security.audit.view', 'security.audit.export',
    'security.privacy.view', 'security.privacy.manage',
    'security.integrations.view', 'security.api_keys.view', 'security.api_keys.manage',
    'security.encryption.view', 'security.encryption.manage', 'security.encryption.rotate',
    'security.incidents.view', 'security.incidents.manage',
    'security.compliance.view', 'security.compliance.manage',
    'security.assurance.view', 'security.assurance.generate', 'security.assurance.download',
    'security.privileged_access.approve',
    'security.view', 'security.manage',
  ],
  business_admin: [
    'users.view.global', 'users.manage', 'users.invite', 'users.roles.change',
    'analytics.view.business', 'analytics.view.projects', 'analytics.view.users', 'reports.view',
    'projects.view', 'projects.create', 'projects.update', 'projects.delete',
    'investments.rights.view', 'investments.rights.manage', 'investments.rights.activate',
    'projects.members.role.change', 'projects.members.remove',
    'files.view', 'files.create', 'files.update', 'files.delete',
    'documents.restore', 'documents.version.restore', 'documents.sign',
    'tasks.view', 'tasks.create', 'tasks.update', 'tasks.delete', 'tasks.assign',
    'companies.view', 'companies.manage', 'calendar.view', 'calendar.manage',
    'security.overview.view', 'security.identity.view',
    'security.audit.view', 'security.privacy.view', 'security.integrations.view',
    'security.incidents.view', 'security.compliance.view',
    'security.assurance.view', 'security.assurance.download',
    'security.view', 'system.settings.edit',
    'evidence.share.create', 'evidence.share.revoke', 'evidence.share.view', 'evidence.share.export',
    'dataroom.access.grant', 'dataroom.access.revoke', 'dataroom.access.view', 'dataroom.access.export',
    'escalations.view', 'escalations.create', 'escalations.acknowledge',
    'escalations.resolve', 'escalations.reassign', 'escalations.cancel',
    'gates.view', 'gates.create',
  ],
  workspace_admin: [
    'users.view.collaborators', 'analytics.view.projects',
    'projects.view', 'projects.create', 'projects.update',
    'investments.rights.view', 'investments.rights.manage',
    'projects.members.role.change', 'projects.members.remove',
    'files.view', 'files.create', 'files.update', 'files.delete',
    'documents.restore', 'documents.version.restore', 'documents.sign',
    'tasks.view', 'tasks.create', 'tasks.update', 'tasks.delete', 'tasks.assign',
    'companies.view', 'calendar.view', 'calendar.manage',
    'security.overview.view', 'security.audit.view', 'security.incidents.view',
    'security.view',
    'evidence.share.create', 'evidence.share.revoke', 'evidence.share.view', 'evidence.share.export',
    'dataroom.access.grant', 'dataroom.access.revoke', 'dataroom.access.view', 'dataroom.access.export',
    'escalations.view', 'escalations.create', 'escalations.acknowledge',
    'escalations.resolve', 'escalations.reassign', 'escalations.cancel',
    'gates.view', 'gates.create',
  ],
};

/**
 * @deprecated Do not use global role→capability mapping.
 * 
 * Scoped relationships (delivery lead, ally, supplier, capital participant) must be resolved
 * with full context:
 * - projectId
 * - companyId  
 * - investmentId
 * - access level
 * - lifecycle status
 *
 * These capabilities should come from a properly scoped resolver, not a global table.
 * See canonicalResolver.ts for the correct pattern.
 */
export const LEGACY_SCOPED_ROLE_CAPABILITIES: Record<string, Capability[]> = {
  project_manager: [
    'users.view.collaborators', 'analytics.view.projects',
    'projects.view', 'projects.create', 'projects.update',
    'investments.rights.view', 'investments.rights.manage',
    'projects.members.role.change', 'projects.members.remove',
    'files.view', 'files.create', 'files.update',
    'documents.restore', 'documents.version.restore', 'documents.sign',
    'tasks.view', 'tasks.create', 'tasks.update', 'tasks.delete', 'tasks.assign',
    'companies.view', 'companies.manage', 'calendar.view', 'calendar.manage',
    'escalations.view', 'escalations.create', 'gates.view', 'gates.create',
  ],
  partner: [
    'users.view.collaborators', 'projects.view', 'files.view', 'files.create',
    'tasks.view', 'tasks.update', 'calendar.view', 'calendar.manage',
    'escalations.create', 'gates.view',
  ],
  vendor: [
    'users.view.collaborators', 'projects.view', 'files.view', 'files.create',
    'tasks.view', 'tasks.update', 'calendar.view', 'calendar.manage',
    'gates.view',
  ],
  investor: [
    'investor.dashboard.view', 'investor.investments.view', 'investor.milestones.view',
    'investor.distributions.view', 'investor.documents.view', 'investor.calendar.view',
    'investor.chat.access', 'investor.security.view', 'investor.settings.manage',
    'investor.reports.view', 'investor.notifications.view', 'calendar.view',
    'tasks.view', 'gates.view',
  ],
};

/**
 * Portal surface capabilities (read/nav surfaces the capital portal shell
 * and /investor/* pages gate on). These are unlocked by holding an ACTIVE
 * capital-participant scoped relationship — resolved per user via
 * scopedRelationshipResolver — not by any global account assignment. Consumers
 * merge these on top of the account capability set when the relationship is
 * present (see useCapabilities + capital app shell).
 */
/** Capital relationship surfaces, granted only through an active scoped relationship. */
export const capitalSurfaceCaps: readonly Capability[] =
  [
    'investor.dashboard.view', 'investor.investments.view', 'investor.milestones.view',
    'investor.distributions.view', 'investor.documents.view', 'investor.calendar.view',
    'investor.chat.access', 'investor.security.view', 'investor.settings.manage',
    'investor.reports.view', 'investor.notifications.view', 'calendar.view',
    'tasks.view', 'gates.view',
  ];

export function getCapabilitiesFor(role: UserRole | string | undefined | null): Capability[] {
  if (!role) return [];
  const mvpRole = resolveMvpAccountRole(role);
  if (mvpRole) return [...MVP_ACCOUNT_CAPABILITIES[mvpRole]];
  const normalized = normalizeAccountRole(role);
  if (!normalized) return []; // Fail closed for unknown roles
  if (normalized in PLATFORM_CAPABILITIES) {
    return PLATFORM_CAPABILITIES[normalized as PlatformRole];
  }
  // Workspace roles only contain admin roles, not scoped relationships
  return WORKSPACE_CAPABILITIES[normalized as WorkspaceRole] ?? [];
}

const PLATFORM_ACCOUNT_ROLE_SET: ReadonlySet<string> = new Set<string>(MVP_PLATFORM_ACCOUNT_ROLES);

export function isPlatformAccountRole(role: string | null | undefined): role is PlatformAccountRole {
  return Boolean(role && PLATFORM_ACCOUNT_ROLE_SET.has(role));
}

/**
 * Canonical ACCOUNT-plane capability resolution. Reads ONLY the canonical
 * `account_role` (+ explicit platform capability assignments for platform
 * staff). It NEVER reads platform_role / workspace_role / role /
 * platform_functions — those are non-authorizing evidence.
 *
 * - platform account role -> reviewed platform base policy + active assignments
 *   (via resolvePlatformCapabilities; sensitive assignments still gated by AAL)
 * - organization account role -> MVP account capabilities
 * - unknown / missing account_role -> [] (fail closed)
 *
 * IMPORTANT: customer RESOURCE authority (projects/files/tasks/investments/etc.)
 * is NOT decided here. Use canResolve(capability, scopedContext) with scoped
 * relationships + resource context for customer-plane decisions.
 */
export function getAccountCapabilities(
  user: {
    account_role?: string | null;
    platform_capability_assignments?: readonly PlatformCapabilityAssignment[] | null;
  } | null | undefined,
  ctx: PlatformCapabilityResolveContext = {},
): Capability[] {
  const accountRole = user?.account_role;
  if (!accountRole) return [];
  if (isPlatformAccountRole(accountRole)) {
    return [...resolvePlatformCapabilities(accountRole, user?.platform_capability_assignments ?? undefined, ctx)];
  }
  const mvpRole = resolveMvpAccountRole(accountRole);
  if (mvpRole && mvpRole in MVP_ACCOUNT_CAPABILITIES) {
    return [...MVP_ACCOUNT_CAPABILITIES[mvpRole]];
  }
  return []; // unknown / legacy account_role fails closed
}

/**
 * Resolve capabilities for the Security Center without collapsing the two
 * authority planes into a single display role.
 *
 * Platform identities use the canonical account role plus lifecycle-managed
 * capability assignments. Workspace Security uses the active administrative
 * workspace role because the database scopes Security RLS to that membership.
 * The canonical account role remains the fail-closed fallback for organisation
 * owners/admins whose membership role has not yet been hydrated in the client.
 */
export function getSecurityCapabilitiesForUser(
  user: {
    account_role?: string | null;
    workspace_role?: string | null;
    platform_capability_assignments?: readonly PlatformCapabilityAssignment[] | null;
  } | null | undefined,
  ctx: PlatformCapabilityResolveContext = {},
): Capability[] {
  if (!user) return [];

  if (isPlatformAccountRole(user.account_role)) {
    return getAccountCapabilities(user, ctx);
  }

  const workspaceRole = user.workspace_role;
  if (workspaceRole && workspaceRole in WORKSPACE_CAPABILITIES) {
    return [...WORKSPACE_CAPABILITIES[workspaceRole as WorkspaceRole]];
  }

  return getAccountCapabilities(user, ctx);
}

/**
 * Get merged capabilities from both workspace_role and project-level role.
 * Uses the union of capabilities from both roles.
 */
export function getMergedCapabilities(
  workspaceRole: WorkspaceRole | PlatformRole | string | undefined | null,
  projectRole: UserRole | string | undefined | null
): Capability[] {
  const workspaceCaps = getCapabilitiesFor(workspaceRole);
  const projectCaps = getCapabilitiesFor(projectRole);
  // Merge and deduplicate
  return [...new Set([...workspaceCaps, ...projectCaps])];
}

// Helper predicates (navigation/page conditions)
export function canAccessUsersRoute(caps: Capability[]): boolean {
  return caps.includes('users.view.global') || caps.includes('users.view.collaborators');
}
export function canManageUsers(caps: Capability[]): boolean {
  return caps.includes('users.manage');
}
export function canInviteUsers(caps: Capability[]): boolean {
  return caps.includes('users.invite');
}
export function canChangeWorkspaceRoles(caps: Capability[]): boolean {
  return caps.includes('users.roles.change');
}
export function canViewBusinessAnalytics(caps: Capability[]): boolean {
  return caps.includes('analytics.view.business');
}
export function canViewProjectAnalytics(caps: Capability[]): boolean {
  return caps.includes('analytics.view.projects');
}
// File predicates
export function canViewFiles(caps: Capability[]): boolean {
  return caps.includes('files.view');
}
export function canViewDocuments(caps: Capability[]): boolean {
  return canViewFiles(caps);
}
export function canCreateFiles(caps: Capability[]): boolean {
  return caps.includes('files.create');
}
export function canUpdateFiles(caps: Capability[]): boolean {
  return caps.includes('files.update') && canGrantDataRoomAccess(caps);
}
export function canDeleteFiles(caps: Capability[]): boolean {
  return caps.includes('files.delete');
}
// Project predicates
export function canViewProjects(caps: Capability[]): boolean {
  return caps.includes('projects.view');
}
export function canCreateProjects(caps: Capability[]): boolean {
  return caps.includes('projects.create');
}
export function canUpdateProjects(caps: Capability[]): boolean {
  return caps.includes('projects.update');
}
export function canDeleteProjects(caps: Capability[]): boolean {
  return caps.includes('projects.delete');
}
// Singular aliases (for consistency with task predicate naming when desired)
export const canViewProject = canViewProjects;
export const canCreateProject = canCreateProjects;
export const canUpdateProject = canUpdateProjects;
export const canDeleteProject = canDeleteProjects;
// Task predicates
export function canViewTasks(caps: Capability[]): boolean {
  return caps.includes('tasks.view');
}
export function canCreateTasks(caps: Capability[]): boolean {
  return caps.includes('tasks.create');
}
export function canUpdateTasks(caps: Capability[]): boolean {
  return caps.includes('tasks.update');
}
export function canDeleteTasks(caps: Capability[]): boolean {
  return caps.includes('tasks.delete');
}
export function canAssignTasks(caps: Capability[]): boolean {
  return caps.includes('tasks.assign');
}
// Calendar predicates
export function canViewCalendar(caps: Capability[]): boolean {
  return caps.includes('calendar.view');
}
export function canManageCalendar(caps: Capability[]): boolean {
  return caps.includes('calendar.manage');
}
export function canViewSystemAnalytics(caps: Capability[]): boolean {
  return caps.includes('analytics.view.system');
}
export function canViewUserAnalytics(caps: Capability[]): boolean {
  return caps.includes('analytics.view.users');
}
export function canViewPlatformControl(caps: Capability[]): boolean {
  return caps.includes('platform.control.view');
}
export function canViewPlatformCustomers(caps: Capability[]): boolean {
  return caps.includes('platform.customers.view');
}
export function canManagePlatformCustomers(caps: Capability[]): boolean {
  return caps.includes('platform.customers.manage');
}
export function canRegisterPlatformCustomers(caps: Capability[]): boolean {
  return caps.includes('platform.customers.register');
}
export function canManagePlatformProvisioning(caps: Capability[]): boolean {
  return caps.includes('platform.provisioning.manage');
}
export function canViewPlatformCommercial(caps: Capability[]): boolean {
  return caps.includes('platform.commercial.view');
}
export function canManagePlatformCommercial(caps: Capability[]): boolean {
  return caps.includes('platform.commercial.manage');
}
export function canApprovePlatformCommercial(caps: Capability[]): boolean {
  return caps.includes('platform.commercial.approve');
}
export function canViewPlatformOperations(caps: Capability[]): boolean {
  return caps.includes('platform.operations.view');
}
export function canManagePlatformOperations(caps: Capability[]): boolean {
  return caps.includes('platform.operations.manage');
}
export function canViewPlatformAccess(caps: Capability[]): boolean {
  return caps.includes('platform.access.view');
}
export function canRequestPlatformAccess(caps: Capability[]): boolean {
  return caps.includes('platform.access.request');
}
export function canApprovePlatformAccess(caps: Capability[]): boolean {
  return caps.includes('platform.access.approve');
}
export function canViewPlatformReleases(caps: Capability[]): boolean {
  return caps.includes('platform.releases.view');
}
export function canManagePlatformReleases(caps: Capability[]): boolean {
  return caps.includes('platform.releases.manage');
}
export function canAssignPlatformRoles(caps: Capability[]): boolean {
  return caps.includes('platform.roles.assign');
}
export function canActivatePlatformRoles(caps: Capability[]): boolean {
  return caps.includes('platform.roles.activate');
}
export function canApprovePlatformRoleActivations(caps: Capability[]): boolean {
  return caps.includes('platform.roles.activate.approve');
}
export function canViewOrganisationCommercial(caps: Capability[]): boolean {
  return caps.includes('commercial.summary.view');
}
export function canViewOrganisationCommercialFinancials(caps: Capability[]): boolean {
  return caps.includes('commercial.pricing.view')
    && caps.includes('commercial.contract.view')
    && caps.includes('commercial.invoices.view');
}
export function canCreateOrganisationCommercialRequest(caps: Capability[]): boolean {
  return caps.includes('commercial.change_request.create');
}
export function canManageOrganisationBillingProfile(caps: Capability[]): boolean {
  return caps.includes('commercial.billing_profile.manage');
}
export function canApproveCustomerPrivilegedAccess(caps: Capability[]): boolean {
  return caps.includes('security.privileged_access.approve');
}
export function canViewSecurity(caps: Capability[]): boolean {
  return caps.includes('security.overview.view') || caps.includes('security.view');
}
export function canManageSecurity(caps: Capability[]): boolean {
  return caps.includes('security.identity.manage')
    || caps.includes('security.privacy.manage')
    || caps.includes('security.integrations.manage')
    || caps.includes('security.api_keys.manage')
    || caps.includes('security.encryption.manage')
    || caps.includes('security.encryption.rotate')
    || caps.includes('security.incidents.manage')
    || caps.includes('security.compliance.manage')
    || caps.includes('security.assurance.generate')
    || caps.includes('security.manage');
}
export function canViewSecurityIdentity(caps: Capability[]): boolean {
  return caps.includes('security.identity.view');
}
export function canManageSecurityIdentity(caps: Capability[]): boolean {
  return caps.includes('security.identity.manage');
}
export function canViewSecurityAudit(caps: Capability[]): boolean {
  return caps.includes('security.audit.view');
}
export function canExportSecurityAudit(caps: Capability[]): boolean {
  return caps.includes('security.audit.export');
}
export function canViewSecurityPrivacy(caps: Capability[]): boolean {
  return caps.includes('security.privacy.view');
}
export function canManageSecurityPrivacy(caps: Capability[]): boolean {
  return caps.includes('security.privacy.manage');
}
export function canViewSecurityIntegrations(caps: Capability[]): boolean {
  return caps.includes('security.integrations.view');
}
export function canManageSecurityIntegrations(caps: Capability[]): boolean {
  return caps.includes('security.integrations.manage');
}
export function canViewSecurityApiKeys(caps: Capability[]): boolean {
  return caps.includes('security.api_keys.view');
}
export function canManageSecurityApiKeys(caps: Capability[]): boolean {
  return caps.includes('security.api_keys.manage');
}
export function canViewSecurityEncryption(caps: Capability[]): boolean {
  return caps.includes('security.encryption.view');
}
export function canManageSecurityEncryption(caps: Capability[]): boolean {
  return caps.includes('security.encryption.manage');
}
export function canRotateSecurityEncryption(caps: Capability[]): boolean {
  return caps.includes('security.encryption.rotate');
}
export function canViewSecurityIncidents(caps: Capability[]): boolean {
  return caps.includes('security.incidents.view');
}
export function canManageSecurityIncidents(caps: Capability[]): boolean {
  return caps.includes('security.incidents.manage');
}
export function canViewSecurityCompliance(caps: Capability[]): boolean {
  return caps.includes('security.compliance.view');
}
export function canManageSecurityCompliance(caps: Capability[]): boolean {
  return caps.includes('security.compliance.manage');
}
export function canViewSecurityAssurance(caps: Capability[]): boolean {
  return caps.includes('security.assurance.view');
}
export function canGenerateSecurityAssurance(caps: Capability[]): boolean {
  return caps.includes('security.assurance.generate');
}
export function canDownloadSecurityAssurance(caps: Capability[]): boolean {
  return caps.includes('security.assurance.download');
}
export function canViewDebug(caps: Capability[]): boolean {
  return caps.includes('debug.view');
}
export function canManageDebug(caps: Capability[]): boolean {
  return caps.includes('debug.manage');
}
export function canEditSystemSettings(caps: Capability[]): boolean {
  return caps.includes('system.settings.edit');
}
export function canManageSystemRoles(caps: Capability[]): boolean {
  return caps.includes('system.roles.manage') || caps.includes('users.roles.change');
}

// Company predicates (CRM)
export function canViewCompanies(caps: Capability[]): boolean {
  return caps.includes('companies.view');
}
export function canManageCompanies(caps: Capability[]): boolean {
  return caps.includes('companies.manage');
}

// The retired Finance workspace never grants revenue-target edits.
export function canEditRevenueTarget(_caps: Capability[]): boolean {
  return false;
}

// Project member predicates (team management)
export function canChangeProjectMemberRole(caps: Capability[]): boolean {
  return caps.includes('projects.members.role.change');
}
export function canRemoveProjectMember(caps: Capability[]): boolean {
  return caps.includes('projects.members.remove');
}

// Evidence pack sharing predicates
export function canShareEvidencePack(caps: Capability[]): boolean {
  return caps.includes('evidence.share.create');
}
export function canRevokeEvidenceShare(caps: Capability[]): boolean {
  return caps.includes('evidence.share.revoke');
}
export function canViewEvidenceShares(caps: Capability[]): boolean {
  return caps.includes('evidence.share.view');
}
export function canExportEvidencePack(caps: Capability[]): boolean {
  return caps.includes('evidence.share.export');
}

// Document restore predicate
export function canRestoreDocuments(caps: Capability[]): boolean {
  return caps.includes('documents.restore');
}

export function canRestoreDocumentVersions(caps: Capability[]): boolean {
  return caps.includes('documents.version.restore');
}

/** True when the role may sign an approved document version. */
export function canSignDocuments(caps: Capability[]): boolean {
  return caps.includes('documents.sign');
}

// Data room access grants predicates
export function canGrantDataRoomAccess(caps: Capability[]): boolean {
  return caps.includes('dataroom.access.grant') || canShareEvidencePack(caps);
}
export function canRevokeDataRoomAccess(caps: Capability[]): boolean {
  return caps.includes('dataroom.access.revoke') || canRevokeEvidenceShare(caps);
}
export function canViewDataRoomAccessGrants(caps: Capability[]): boolean {
  return caps.includes('dataroom.access.view') || canViewEvidenceShares(caps);
}
export function canExportDataRoomAccessReceipt(caps: Capability[]): boolean {
  return caps.includes('dataroom.access.export');
}

// Escalation workflow predicates
export function canViewEscalations(caps: Capability[]): boolean {
  return caps.includes('escalations.view') || caps.includes('escalations.admin');
}
export function canCreateEscalations(caps: Capability[]): boolean {
  return caps.includes('escalations.create') || caps.includes('escalations.admin');
}
export function canAcknowledgeEscalations(caps: Capability[]): boolean {
  return caps.includes('escalations.acknowledge') || caps.includes('escalations.admin');
}
export function canResolveEscalations(caps: Capability[]): boolean {
  return caps.includes('escalations.resolve') || caps.includes('escalations.admin');
}
export function canReassignEscalations(caps: Capability[]): boolean {
  return caps.includes('escalations.reassign') || caps.includes('escalations.admin');
}
export function canCancelEscalations(caps: Capability[]): boolean {
  return caps.includes('escalations.cancel') || caps.includes('escalations.admin');
}

// Investor dashboard predicate
export function canViewInvestorDashboard(
  caps: Capability[],
): boolean {
  const required =
    'investor.dashboard.view';
  return caps.includes(required);
}

// Investor-scoped predicates
export function canViewInvestorInvestments(
  caps: Capability[],
): boolean {
  const required =
    'investor.investments.view';
  return caps.includes(required);
}
export function canViewInvestorMilestones(
  caps: Capability[],
): boolean {
  const required =
    'investor.milestones.view';
  return caps.includes(required);
}
export function canViewInvestorDistributions(
  caps: Capability[],
): boolean {
  const required =
    'investor.distributions.view';
  return caps.includes(required);
}
export function canViewInvestorDocuments(
  caps: Capability[],
): boolean {
  const required =
    'investor.documents.view';
  return caps.includes(required);
}
export function canAccessInvestorChat(
  caps: Capability[],
): boolean {
  const required =
    'investor.chat.access';
  return caps.includes(required);
}
export function canViewInvestorCalendar(
  caps: Capability[],
): boolean {
  const required =
    'investor.calendar.view';
  return caps.includes(required);
}
export function canViewInvestorSecurity(
  caps: Capability[],
): boolean {
  const required =
    'investor.security.view';
  return caps.includes(required);
}
export function canManageInvestorSettings(
  caps: Capability[],
): boolean {
  const required =
    'investor.settings.manage';
  return caps.includes(required);
}
export function canViewInvestorReports(
  caps: Capability[],
): boolean {
  const required =
    'investor.reports.view';
  return caps.includes(required);
}
export function canViewInvestorNotifications(
  caps: Capability[],
): boolean {
  const required =
    'investor.notifications.view';
  return caps.includes(required);
}

/** Check if the user uses the legacy capital-participant account token. */
function checkCapitalParticipantToken(
  accountToken: string | undefined | null,
): boolean {
  const legacyToken = `${'in'}${'vestor'}`;
  return accountToken === legacyToken;
}
export { checkCapitalParticipantToken as isInvestorRole };

/**
 * Canonical capital-participant detection for active runtime users.
 * Supports legacy account tokens for compatibility while prioritizing
 * active scoped-relationship facts.
 */
export function isInvestorUser(
  user: {
    role?: string | null;
    scopedRelationships?: readonly {
      type?: string | null;
      status?: string | null;
    }[] | null;
  } | null | undefined,
): boolean {
  if (!user) return false;
  return (user.scopedRelationships ?? []).some((relationship) => {
    if (relationship.status !== 'active') return false;
    return relationship.type === 'investor_representative'
      || relationship.type === 'investment_manager'
      || relationship.type === 'reporting_recipient';
  });
}

// Governance gate predicates
export function canViewGates(caps: Capability[]): boolean {
  return caps.includes('gates.view');
}
export function canCreateGates(caps: Capability[]): boolean {
  return caps.includes('gates.create');
}
export function canConfigureGates(caps: Capability[]): boolean {
  return caps.includes('gates.configure');
}
export function canLockGates(caps: Capability[]): boolean {
  return caps.includes('gates.lock');
}
export function canAmendGates(caps: Capability[]): boolean {
  return caps.includes('gates.amend');
}
export function canReviewGates(caps: Capability[]): boolean {
  return caps.includes('gates.review');
}

// Helper to check if user has any capability from a list
export function hasAnyCapability(caps: Capability[], required: Capability[]): boolean {
  return required.some((cap) => caps.includes(cap));
}
